import React from "react";
import PropTypes from "prop-types";
import {
    Empty,
    Spin,
    Button,
    Icon,
    Modal,
    Input,
    InputNumber,
    Pagination,
    Popover,
    message
} from "antd";
import CardBlock from "./card-block";
import { addBrandMix, saveBrandMixRemark } from "services/case-service";

/**
 * Brand Mix — manufacturers/suppliers from the manufacturer-GST pull with their
 * share of total supply-chain (taxable) value: paginated + searchable table, an
 * FY-wise breakup per row, dependence highlighting, and an "Add brand" modal.
 *
 * Extracted from company-detail-section so the SAME table can be rendered in more
 * than one place (Company Detail and the Credit TVR) without a second copy of the
 * logic — both mount this component and share its behaviour and its data source.
 *
 * Owns only the add-brand form state. The rows, paging and search live with the
 * host (they are server-driven), and come in as props.
 */

// GSTIN is optional, but if provided it must be a full valid 15-char GSTIN.
const GSTIN_FULL_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/i;

// Identity of a brand/supplier for dependence matching: name + PAN, both
// required. Normalised (upper-cased, inner whitespace collapsed) so "Om Sai
// Marketing" and "OM  SAI MARKETING" are the same seller. Returns null when
// either half is missing — a half-match must not highlight a row.
// Compact rupees for the FY chips, where a full "₹35,83,675" would blow the
// column width: ≥1 Cr → "₹3.58 Cr", ≥1 L → "₹35.8 L", else "₹8,420".
function compactMoney(value) {
    const n = Number(value);
    if (!isFinite(n) || Number.isNaN(n)) {
        return null;
    }
    if (Math.abs(n) >= 1e7) {
        return `₹${(n / 1e7).toFixed(2)} Cr`;
    }
    if (Math.abs(n) >= 1e5) {
        return `₹${(n / 1e5).toFixed(1)} L`;
    }
    return "₹" + Math.round(n).toLocaleString("en-IN");
}

function brandKey(name, pan) {
    const n = String(name || "")
        .trim()
        .replace(/\s+/g, " ")
        .toUpperCase();
    const p = String(pan || "")
        .trim()
        .toUpperCase();
    return n && p ? `${n}|${p}` : null;
}

// Last N Indian financial years (Apr–Mar), most-recent first.
// e.g. in Jul-2026 → ["2026-27", "2025-26", "2024-25", "2023-24"].
function lastFinancialYears(n) {
    const now = new Date();
    // Indian FY starts in April (month index 3). Before April we are still
    // in the previous FY.
    const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const out = [];
    for (let i = 0; i < n; i += 1) {
        const s = startYear - i;
        out.push(`${s}-${String((s + 1) % 100).padStart(2, "0")}`);
    }
    return out;
}

// The four FYs the user can optionally break the taxable value down into.
const BRAND_FY_YEARS = lastFinancialYears(4);

const EMPTY_BRAND_FORM = {
    gst_no: "",
    name: "",
    total_taxable_value_in_rs: null,
    // Per-FY breakup keyed by FY label, e.g.
    // { "2025-26": { total_taxable_value_in_rs, proportion_of_total_supplychain_value_in_percent } }
    fy: {}
};


class BrandMixCard extends React.Component {
    state = {
        brandModalOpen: false,
        brandSubmitting: false,
        brandForm: { ...EMPTY_BRAND_FORM },
        // per-field inline validation errors, keyed by form field
        brandErrors: {},
        // Brand-mix remarks: the PAN being edited, the draft text, and the PAN
        // mid-save. `remarkOverrides` holds saved values keyed by PAN so the cell
        // reflects the save immediately (the row list is refetched by the host
        // only on add/search/page, not on a remark save).
        remarkEditPan: null,
        remarkDraft: "",
        remarkSavingPan: null,
        remarkOverrides: {}
    };

    // Brand mix remarks (and Add Brand) are only actionable at the credit
    // stages — same gate used elsewhere in Credit Checker.
    canEditBrandMix = () => [7, 8].includes(Number(this.props.stageId));

    // The remark to show for a row: a just-saved override wins over the API value.
    brandRemark = row => {
        const pan = row && row.pan;
        if (pan && this.state.remarkOverrides[pan] !== undefined) {
            return this.state.remarkOverrides[pan];
        }
        return (row && row.remarks) || "";
    };

    openRemarkEdit = row =>
        this.setState({
            remarkEditPan: row.pan,
            remarkDraft: this.brandRemark(row)
        });

    closeRemarkEdit = () =>
        this.setState({ remarkEditPan: null, remarkDraft: "" });

    onRemarkDraftChange = e => this.setState({ remarkDraft: e.target.value });

    saveRemark = row => {
        const { caseId } = this.props;
        const pan = row && row.pan;
        // PAN is the row's key for the remarks endpoint; without it there is
        // nothing to save against.
        if (!caseId || !pan) {
            message.error("Cannot save remark: this row has no PAN");
            return;
        }
        const remarks = (this.state.remarkDraft || "").trim();
        this.setState({ remarkSavingPan: pan });
        saveBrandMixRemark(caseId, pan, remarks).then(
            () => {
                this.setState(prev => ({
                    remarkSavingPan: null,
                    remarkEditPan: null,
                    remarkDraft: "",
                    remarkOverrides: {
                        ...prev.remarkOverrides,
                        [pan]: remarks
                    }
                }));
                message.success("Remark saved");
            },
            () => {
                this.setState({ remarkSavingPan: null });
                message.error("Failed to save remark");
            }
        );
    };

    renderRemarkCell(row) {
        const pan = row && row.pan;
        // The remarks endpoint is keyed by PAN — a manual row with no PAN can't
        // carry one, so show why rather than a dead pencil.
        if (!pan) {
            return (
                <span className={"cc-kyc-empty"} title={"No PAN on this row"}>
                    —
                </span>
            );
        }
        const canEdit = this.canEditBrandMix();
        const isEditing = canEdit && this.state.remarkEditPan === pan;
        const isSaving = this.state.remarkSavingPan === pan;
        if (isEditing) {
            return (
                <div className={"cc-brandmix__remark-edit"}>
                    <Input.TextArea
                        autoFocus
                        autoSize={{ minRows: 2, maxRows: 5 }}
                        value={this.state.remarkDraft}
                        onChange={this.onRemarkDraftChange}
                        placeholder={"Add a remark"}
                        disabled={isSaving}
                    />
                    <div className={"cc-brandmix__remark-actions"}>
                        <Button
                            size={"small"}
                            type={"primary"}
                            loading={isSaving}
                            // Nothing to save on a blank/whitespace-only remark.
                            disabled={!this.state.remarkDraft.trim()}
                            onClick={() => this.saveRemark(row)}
                        >
                            Save
                        </Button>
                        <Button
                            size={"small"}
                            disabled={isSaving}
                            onClick={this.closeRemarkEdit}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            );
        }
        const remark = this.brandRemark(row);
        if (!canEdit) {
            return remark ? (
                <span className={"cc-brandmix__remark-text"}>{remark}</span>
            ) : (
                <span className={"cc-kyc-empty"}>—</span>
            );
        }
        return (
            <div
                className={"cc-brandmix__remark"}
                onClick={() => this.openRemarkEdit(row)}
                title={remark ? "Edit remark" : "Add remark"}
            >
                {remark ? (
                    <span className={"cc-brandmix__remark-text"}>{remark}</span>
                ) : (
                    <span className={"cc-kyc-empty"}>Add remark</span>
                )}
                <Icon type={"edit"} className={"cc-brandmix__remark-icon"} />
            </div>
        );
    }

    openBrandModal = () =>
        this.setState({
            brandModalOpen: true,
            brandForm: { ...EMPTY_BRAND_FORM },
            brandErrors: {}
        });

    closeBrandModal = () =>
        this.setState({ brandModalOpen: false, brandSubmitting: false });

    // Validates a single field's value; returns an error string or "".
    validateBrandField = (key, form) => {
        if (key === "name") {
            return form.name && form.name.trim() ? "" : "Brand name is required";
        }
        if (key === "gst_no") {
            const gstin = (form.gst_no || "").trim().toUpperCase();
            if (gstin && !GSTIN_FULL_REGEX.test(gstin)) {
                return "Enter a valid 15-character GSTIN";
            }
            return "";
        }
        if (key === "total_taxable_value_in_rs") {
            const v = form.total_taxable_value_in_rs;
            if (v === null || v === undefined || v === "") {
                return "Taxable value is required";
            }
            if (Number(v) < 0) {
                return "Must be zero or positive";
            }
            return "";
        }
        return "";
    };

    // Updates a field and re-validates it live (clears/sets its inline error).
    setBrandField = (key, value) =>
        this.setState(prev => {
            const brandForm = { ...prev.brandForm, [key]: value };
            return {
                brandForm,
                brandErrors: {
                    ...prev.brandErrors,
                    [key]: this.validateBrandField(key, brandForm)
                }
            };
        });

    // Updates a single FY's sub-field (taxable value / proportion) in the
    // per-year breakup.
    setBrandFy = (fy, sub, value) =>
        this.setState(prev => ({
            brandForm: {
                ...prev.brandForm,
                fy: {
                    ...prev.brandForm.fy,
                    [fy]: { ...(prev.brandForm.fy[fy] || {}), [sub]: value }
                }
            }
        }));

    // Builds the fy_wise payload from the per-year inputs. Only FYs the user
    // actually filled (either field) are included; blank sub-fields default to 0.
    buildFyWise = fy => {
        const out = {};
        BRAND_FY_YEARS.forEach(year => {
            const row = fy[year] || {};
            const total = row.total_taxable_value_in_rs;
            const prop = row.proportion_of_total_supplychain_value_in_percent;
            const hasTotal = total !== null && total !== undefined && total !== "";
            const hasProp = prop !== null && prop !== undefined && prop !== "";
            if (hasTotal || hasProp) {
                out[year] = {
                    total_taxable_value_in_rs: hasTotal ? Number(total) : 0,
                    proportion_of_total_supplychain_value_in_percent: hasProp
                        ? Number(prop)
                        : 0
                };
            }
        });
        return out;
    };

    handleAddBrand = () => {
        const { brandForm } = this.state;
        const { caseId, onBrandMixAdded } = this.props;
        if (!caseId) {
            return;
        }
        // Validate every field up-front; surface all errors inline at once.
        const brandErrors = {};
        ["name", "gst_no", "total_taxable_value_in_rs"].forEach(key => {
            const err = this.validateBrandField(key, brandForm);
            if (err) {
                brandErrors[key] = err;
            }
        });
        if (Object.keys(brandErrors).length) {
            this.setState({ brandErrors });
            return;
        }
        const gstin = (brandForm.gst_no || "").trim().toUpperCase();
        this.setState({ brandSubmitting: true });
        // State is optional. fy_wise carries the (optional) per-year breakup.
        addBrandMix(caseId, {
            gst_no: gstin,
            name: brandForm.name,
            total_taxable_value_in_rs: brandForm.total_taxable_value_in_rs,
            fy_wise: this.buildFyWise(brandForm.fy)
        })
            .then(() => {
                message.success("Brand added");
                this.setState({ brandModalOpen: false, brandSubmitting: false });
                if (onBrandMixAdded) {
                    onBrandMixAdded();
                }
            })
            .catch(() => {
                message.error("Failed to add brand");
                this.setState({ brandSubmitting: false });
            });
    };

    // Brand mix — manufacturers/suppliers from the GST pull with their share of
    // total supply-chain (taxable) value. Rows come from the
    // manufacturer-gst-pull API (paginated results, already flattened upstream).
    // A tinted avatar chip carrying the brand's initials — cycles through a few
    // on-theme hues so consecutive rows read as distinct.
    brandAvatar(name, index) {
        const palette = [
            "#1f6feb",
            "#00a389",
            "#b8860b",
            "#8250df",
            "#d1495b",
            "#2f81f7"
        ];
        const bg = palette[index % palette.length];
        const initials = (name || "?")
            .split(/\s+/)
            .slice(0, 2)
            .map(w => w.charAt(0))
            .join("")
            .toUpperCase();
        return (
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: bg + "1a",
                    color: bg,
                    fontSize: 11,
                    fontWeight: 700,
                    flex: "0 0 auto"
                }}
            >
                {initials}
            </span>
        );
    }

    // Per-brand FY split as compact chips under the name. `fyColumns` is the
    // union of FY keys across every row on the page (see renderBrandMix), so all
    // rows lay out on the SAME columns: an FY a row has no data for renders an
    // empty slot rather than shifting the later years left. That keeps 24-25 /
    // 25-26 / 26-27 vertically aligned down the table.
    renderFyChips(fyWise, fmt, fyColumns) {
        const columns = fyColumns || [];
        const data = fyWise && typeof fyWise === "object" ? fyWise : {};
        if (!columns.length || !Object.keys(data).length) {
            return null;
        }
        // Cap the inline columns; any further FYs collapse into a "+N" chip
        // whose popover lists that row's values, so long histories stay compact.
        const MAX_FY_CHIPS = 5;
        const chip = fy => {
            const d = data[fy];
            const shortFy = fy
                .split("-")
                .map(p => p.slice(-2))
                .join("-"); // "2024-25" -> "24-25"
            // No data for this FY on this row: hold the column, draw nothing.
            if (!d) {
                return (
                    <span
                        key={fy}
                        className={"cc-brandmix-fy__slot"}
                        aria-hidden={"true"}
                    />
                );
            }
            const pct = d.proportion_of_total_supplychain_value_in_percent;
            const pctText = pct === null || pct === undefined ? "—" : pct + "%";
            // Amount is shown ON the chip (not only in a tooltip) — the FY split
            // is read alongside the share, so hiding it behind a hover made the
            // table unusable at a glance. Compact ₹ keeps the column narrow; the
            // exact rupee figure stays in the title for anyone who needs it.
            const amount = compactMoney(d.total_taxable_value_in_rs);
            return (
                <span key={fy} className={"cc-brandmix-fy__slot"}>
                    <span
                        className={"cc-brandmix-fy__chip"}
                        title={`FY ${fy}: ${fmt(
                            d.total_taxable_value_in_rs
                        )} · ${pctText}`}
                    >
                        <span className={"cc-brandmix-fy__yr"}>{shortFy}</span>
                        <span className={"cc-brandmix-fy__pct"}>{pctText}</span>
                        {amount ? (
                            <span className={"cc-brandmix-fy__amt"}>
                                {amount}
                            </span>
                        ) : null}
                    </span>
                </span>
            );
        };
        const visible = columns.slice(0, MAX_FY_CHIPS);
        const rest = columns.slice(MAX_FY_CHIPS).filter(fy => data[fy]);
        return (
            <div
                className={"cc-brandmix-fy"}
                style={{
                    gridTemplateColumns: `repeat(${visible.length +
                        (rest.length ? 1 : 0)}, minmax(0, 1fr))`
                }}
            >
                {visible.map(chip)}
                {rest.length ? (
                    <Popover
                        placement={"bottomLeft"}
                        title={"More financial years"}
                        content={
                            <div className={"cc-brandmix-fy cc-brandmix-fy--pop"}>
                                {rest.map(chip)}
                            </div>
                        }
                    >
                        <span
                            className={
                                "cc-brandmix-fy__chip cc-brandmix-fy__chip--more"
                            }
                        >
                            +{rest.length}
                        </span>
                    </Popover>
                ) : null}
            </div>
        );
    }

    // Keys of the sellers the case is dependent on, so the Brand Mix rows that
    // ARE those sellers can be highlighted. caseDetailFields.dependence carries
    // a `match` (the brand-mix row it was matched to); a row counts as the same
    // seller only when BOTH name and PAN agree, compared case/space-insensitively.
    dependenceKeys() {
        const { dependence } = this.props;
        const rows =
            dependence && dependence.toJS ? dependence.toJS() : dependence;
        const keys = new Set();
        (rows || []).forEach(row => {
            const match = row && row.match;
            const key = match && brandKey(match.name, match.pan);
            if (key) {
                keys.add(key);
            }
        });
        return keys;
    }

    renderBrandMix() {
        const {
            brandMix,
            brandMixLoading,
            caseId,
            brandMixCount,
            brandMixPage,
            brandMixPageSize,
            brandMixSearch,
            onBrandMixPageChange,
            onBrandMixSearch
        } = this.props;
        const canAddBrand = this.canEditBrandMix();
        const rows = brandMix || [];
        const pageSize = brandMixPageSize || 10;
        const total =
            brandMixCount !== null && brandMixCount !== undefined
                ? brandMixCount
                : rows.length;
        const fmt = value => {
            const n = Number(value);
            if (!isFinite(n) || Number.isNaN(n)) {
                return "—";
            }
            return "₹" + Math.round(n).toLocaleString("en-IN");
        };
        const pctOf = row => {
            const p = row.proportion_of_total_supplychain_value_in_percent;
            return p === null || p === undefined ? null : Number(p);
        };
        const depKeys = this.dependenceKeys();
        // Shared FY columns for every row on this page: the union of all rows'
        // fy_wise keys, chronological. Rows render one slot per column so the
        // same FY sits in the same place down the table.
        const fyColumns = Array.from(
            rows.reduce((set, row) => {
                const fy = row.fy_wise;
                if (fy && typeof fy === "object") {
                    Object.keys(fy).forEach(k => set.add(k));
                }
                return set;
            }, new Set())
        ).sort();
        return (
            <CardBlock
                icon={"tags"}
                title={"Brand/Seller mix"}
                subtitle={"Suppliers by share of total purchases (GST pull)"}
                action={
                    <span className={"cc-brandmix__actions"}>
                        <Input.Search
                            size={"small"}
                            allowClear
                            placeholder={"Search Name / PAN"}
                            defaultValue={brandMixSearch || ""}
                            disabled={!caseId}
                            style={{ width: 220 }}
                            // Searches server-side across every page, so it fires
                            // on enter / clear rather than on each keystroke.
                            onSearch={value =>
                                onBrandMixSearch && onBrandMixSearch(value)
                            }
                        />
                        <Button
                            size={"small"}
                            icon={"plus"}
                            disabled={!caseId || !canAddBrand}
                            onClick={this.openBrandModal}
                        >
                            Add Brand
                        </Button>
                    </span>
                }
            >
                <Spin spinning={Boolean(brandMixLoading)}>
                    {rows.length ? (
                        <React.Fragment>
                            <div className={"cc-scroll-table"}>
                                <table className={"cc-kyc-table cc-brandmix"}>
                                    <thead>
                                        <tr>
                                            <th>Brand / Supplier</th>
                                            <th>State</th>
                                            <th style={{ minWidth: 220 }}>
                                            FY-wise Share
                                            </th>
                                            <th style={{ textAlign: "right" }}>
                                            Taxable Value
                                            </th>
                                            <th style={{ width: 210 }}>Share</th>
                                            <th style={{ minWidth: 220 }}>
                                            Remarks
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, index) => {
                                            const pct = pctOf(row);
                                            // Progress bar: fill = the actual share
                                            // out of 100 (clamped), so a 0.11% brand
                                            // reads as a sliver and a 90% brand as a
                                            // near-full bar. A tiny non-zero share
                                            // still shows a 1% sliver for visibility.
                                            const width =
                                            pct !== null && pct > 0
                                                ? Math.min(
                                                    100,
                                                    Math.max(1, pct)
                                                )
                                                : 0;
                                            const key = brandKey(
                                                row.name,
                                                row.pan
                                            );
                                            const isDependence = Boolean(
                                                key && depKeys.has(key)
                                            );
                                            return (
                                                <tr
                                                    key={
                                                        row.gst_no ||
                                                    row.id ||
                                                    index
                                                    }
                                                    className={
                                                        isDependence
                                                            ? "cc-brandmix__row--dep"
                                                            : undefined
                                                    }
                                                    title={
                                                        isDependence
                                                            ? "Dependence seller"
                                                            : undefined
                                                    }
                                                >
                                                    <td>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems:
                                                                "center",
                                                                gap: 10
                                                            }}
                                                        >
                                                            {this.brandAvatar(
                                                                row.name,
                                                                index
                                                            )}
                                                            <div>
                                                                <div
                                                                    className={
                                                                        "cc-brandmix__name"
                                                                    }
                                                                    style={{
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    {row.name ||
                                                                    "—"}
                                                                    {isDependence ? (
                                                                        <span
                                                                            className={
                                                                                "cc-brandmix__dep-tag"
                                                                            }
                                                                        >
                                                                        Dependence
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                                {row.gst_no ? (
                                                                    <div
                                                                        style={{
                                                                            fontSize: 11,
                                                                            color:
                                                                            "#8a8f99",
                                                                            letterSpacing:
                                                                            "0.02em"
                                                                        }}
                                                                    >
                                                                        {row.gst_no}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {row.state_name || "—"}
                                                    </td>
                                                    <td>
                                                        {this.renderFyChips(
                                                            row.fy_wise,
                                                            fmt,
                                                            fyColumns
                                                        ) || (
                                                            <span
                                                                className={
                                                                    "cc-kyc-empty"
                                                                }
                                                            >
                                                            —
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: "right"
                                                        }}
                                                    >
                                                        {fmt(
                                                            row.total_taxable_value_in_rs
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems:
                                                                "center",
                                                                gap: 8
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    flex: 1,
                                                                    height: 6,
                                                                    background:
                                                                    "#eef0f4",
                                                                    borderRadius: 4,
                                                                    overflow:
                                                                    "hidden"
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width:
                                                                        width +
                                                                        "%",
                                                                        height:
                                                                        "100%",
                                                                        borderRadius: 4,
                                                                        background:
                                                                        "linear-gradient(90deg,#1f6feb,#4b9bff)"
                                                                    }}
                                                                />
                                                            </div>
                                                            <span
                                                                style={{
                                                                    minWidth: 46,
                                                                    textAlign:
                                                                    "right",
                                                                    fontWeight: 600,
                                                                    fontVariantNumeric:
                                                                    "tabular-nums"
                                                                }}
                                                            >
                                                                {pct !== null
                                                                    ? pct + "%"
                                                                    : "—"}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {this.renderRemarkCell(
                                                            row
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {total > 0 ? (
                                <div className={"cc-brandmix-pager"}>
                                    <span className={"cc-brandmix-pager__count"}>
                                        {total} brand{total === 1 ? "" : "s"}
                                    </span>
                                    <Pagination
                                        size={"small"}
                                        current={brandMixPage || 1}
                                        pageSize={pageSize}
                                        total={total}
                                        onChange={onBrandMixPageChange}
                                        showSizeChanger={false}
                                        hideOnSinglePage={false}
                                    />
                                </div>
                            ) : null}
                        </React.Fragment>
                    ) : (
                        !brandMixLoading && (
                            <Empty
                                description={
                                    brandMixSearch
                                        ? `No brands match "${brandMixSearch}"`
                                        : "No brand mix data yet"
                                }
                            />
                        )
                    )}
                </Spin>
            </CardBlock>
        );
    }

    // Change-ASM modal: pick one of the pincode-nearest ASMs for this case.

    renderBrandModal() {
        const { brandForm, brandErrors } = this.state;
        const field = (label, node, error, required) => (
            <div style={{ marginBottom: 12 }}>
                <div
                    style={{
                        fontSize: 12,
                        color: "#5f6470",
                        marginBottom: 4,
                        fontWeight: 600
                    }}
                >
                    {label}
                    {required ? (
                        <span style={{ color: "#ff4d4f" }}> *</span>
                    ) : null}
                </div>
                {node}
                {error ? (
                    <div
                        style={{
                            color: "#ff4d4f",
                            fontSize: 12,
                            marginTop: 4
                        }}
                    >
                        {error}
                    </div>
                ) : null}
            </div>
        );
        return (
            <Modal
                title={"Add Brand"}
                visible={this.state.brandModalOpen}
                onOk={this.handleAddBrand}
                okText={"Add"}
                confirmLoading={this.state.brandSubmitting}
                onCancel={this.closeBrandModal}
                destroyOnClose
            >
                {field(
                    "GSTIN",
                    <Input
                        value={brandForm.gst_no}
                        maxLength={15}
                        onChange={e =>
                            this.setBrandField(
                                "gst_no",
                                (e.target.value || "").toUpperCase()
                            )
                        }
                        placeholder={"e.g. 09AABCB0537F1Z2"}
                    />,
                    brandErrors.gst_no
                )}
                {field(
                    "Brand Name",
                    <Input
                        value={brandForm.name}
                        onChange={e =>
                            this.setBrandField("name", e.target.value)
                        }
                        placeholder={"e.g. Honda Motor"}
                    />,
                    brandErrors.name,
                    true
                )}
                {field(
                    "Taxable Value",
                    <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        value={brandForm.total_taxable_value_in_rs}
                        onChange={v =>
                            this.setBrandField("total_taxable_value_in_rs", v)
                        }
                        placeholder={"Total taxable value (₹)"}
                    />,
                    brandErrors.total_taxable_value_in_rs,
                    true
                )}

                <div
                    style={{
                        fontSize: 12,
                        color: "#5f6470",
                        fontWeight: 600,
                        margin: "4px 0 2px"
                    }}
                >
                    FY-wise breakup
                    <span
                        style={{
                            color: "#8a8f99",
                            fontWeight: 400,
                            marginLeft: 6
                        }}
                    >
                        optional — fill any of the last 4 years
                    </span>
                </div>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "64px 1fr 1fr",
                        gap: "6px 8px",
                        alignItems: "center",
                        marginBottom: 6
                    }}
                >
                    <span />
                    <span style={{ fontSize: 11, color: "#8a8f99" }}>
                        Taxable Value (₹)
                    </span>
                    <span style={{ fontSize: 11, color: "#8a8f99" }}>
                        Share (%)
                    </span>
                    {BRAND_FY_YEARS.map(year => {
                        const row = brandForm.fy[year] || {};
                        return (
                            <React.Fragment key={year}>
                                <span
                                    style={{ fontSize: 12, fontWeight: 600 }}
                                >
                                    {year}
                                </span>
                                <InputNumber
                                    style={{ width: "100%" }}
                                    min={0}
                                    value={row.total_taxable_value_in_rs}
                                    onChange={v =>
                                        this.setBrandFy(
                                            year,
                                            "total_taxable_value_in_rs",
                                            v
                                        )
                                    }
                                    placeholder={"Taxable ₹"}
                                />
                                <InputNumber
                                    style={{ width: "100%" }}
                                    min={0}
                                    max={100}
                                    value={
                                        row.proportion_of_total_supplychain_value_in_percent
                                    }
                                    onChange={v =>
                                        this.setBrandFy(
                                            year,
                                            "proportion_of_total_supplychain_value_in_percent",
                                            v
                                        )
                                    }
                                    placeholder={"Share %"}
                                />
                            </React.Fragment>
                        );
                    })}
                </div>
            </Modal>
        );
    }

    render() {
        return (
            <React.Fragment>
                {this.renderBrandMix()}
                {this.renderBrandModal()}
            </React.Fragment>
        );
    }
}

BrandMixCard.propTypes = {
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Gates "Add Brand" to the credit stages (7, 8).
    stageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    brandMix: PropTypes.array,
    brandMixLoading: PropTypes.bool,
    brandMixCount: PropTypes.number,
    brandMixPage: PropTypes.number,
    brandMixPageSize: PropTypes.number,
    brandMixSearch: PropTypes.string,
    onBrandMixPageChange: PropTypes.func,
    onBrandMixSearch: PropTypes.func,
    onBrandMixAdded: PropTypes.func,
    // caseDetailFields.dependence — used to highlight rows that ARE the sellers
    // the case depends on.
    dependence: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
};

export default BrandMixCard;
